import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { influencerLogin, influencerSetPassword } from '../lib/influencer-api';
import logo from '../assets/logo.png';

interface Props {
  setIsInfluencerLoggedIn: (val: boolean) => void;
}

export default function InfluencerLogin({ setIsInfluencerLoggedIn }: Props) {
  const [mode, setMode] = useState<'login' | 'setup'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      await influencerLogin(email, password);
      setIsInfluencerLoggedIn(true);
      navigate('/influencer/dashboard');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed. Check your credentials.');
    } finally { setLoading(false); }
  };

  const handleSetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(''); setSuccess('');
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    setLoading(true);
    try {
      await influencerSetPassword(email, code, password);
      setSuccess('Password set successfully! You can now log in.');
      setMode('login');
      setPassword(''); setConfirmPassword('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to set password. Check your email and code.');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={logo} alt="EatMeat" className="w-16 h-16 rounded-full mx-auto mb-4 shadow-lg" />
          <h1 className="text-3xl font-bold text-text">Influencer Portal</h1>
          <p className="text-gray-400 mt-2">Track referrals, commissions & promo materials</p>
        </div>

        {/* Card */}
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-100">
            <button
              onClick={() => { setMode('login'); setError(''); setSuccess(''); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${mode === 'login' ? 'text-primary border-b-2 border-primary bg-orange-50/50' : 'text-gray-400 hover:text-gray-600'}`}
            >
              Log In
            </button>
            <button
              onClick={() => { setMode('setup'); setError(''); setSuccess(''); }}
              className={`flex-1 py-4 text-sm font-semibold transition-colors ${mode === 'setup' ? 'text-primary border-b-2 border-primary bg-orange-50/50' : 'text-gray-400 hover:text-gray-600'}`}
            >
              First Time Setup
            </button>
          </div>

          <div className="p-8">
            {/* Login Form */}
            {mode === 'login' && (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Email Address</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">📧</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="you@example.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Password</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">🔒</span>
                    <input type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="w-full pl-12 pr-12 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="••••••••" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500 text-lg">
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>
                {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2"><span>⚠️</span>{error}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-primary to-orange-500 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 text-base">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span> Logging in...
                    </span>
                  ) : 'Log In'}
                </button>
                <div className="text-center">
                  <a href="/influencer/forgot-password" className="text-xs text-gray-500 hover:text-primary font-medium">
                    Forgot your password?
                  </a>
                </div>
              </form>
            )}

            {/* Setup Form */}
            {mode === 'setup' && (
              <form onSubmit={handleSetPassword} className="space-y-5">
                <div className="bg-blue-50 text-blue-700 text-sm px-4 py-3 rounded-xl flex items-center gap-2">
                  <span>💡</span> Enter your email and referral code to set your password.
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Email Address</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">📧</span>
                    <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="you@example.com" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Referral Code</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">🔑</span>
                    <input type="text" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-mono tracking-widest text-lg" placeholder="GGC10" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Create Password</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">🔒</span>
                    <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="Min 6 characters" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-text mb-2">Confirm Password</label>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 text-lg">🔒</span>
                    <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} required
                      className="w-full pl-12 pr-4 py-3.5 rounded-xl border border-gray-200 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all" placeholder="••••••••" />
                  </div>
                </div>
                {error && <div className="bg-red-50 text-red-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2"><span>⚠️</span>{error}</div>}
                {success && <div className="bg-green-50 text-green-600 text-sm px-4 py-3 rounded-xl flex items-center gap-2"><span>✅</span>{success}</div>}
                <button type="submit" disabled={loading}
                  className="w-full bg-gradient-to-r from-primary to-orange-500 text-white py-3.5 rounded-xl font-semibold hover:shadow-lg hover:shadow-primary/25 transition-all disabled:opacity-50 text-base">
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span> Setting up...
                    </span>
                  ) : 'Set Password'}
                </button>
              </form>
            )}
          </div>
        </div>

        <p className="text-center text-xs text-gray-400 mt-6">
          EatMeat Influencer Program • <a href="mailto:support@eatmeatapp.com" className="text-primary hover:underline">Contact Support</a>
        </p>
      </div>
    </div>
  );
}
